import { observable, action, makeObservable } from "mobx";
import Axios, { AxiosProgressEvent, CancelTokenSource } from "axios";
import { getUid } from "utils/uid";
import { fileDownloadStore } from "Pages/Project/Documents/FileDownload/FileDownloadStore";

/**
 * Class to log network progress and cancel network calls
 */
export class ActionProgressStore {
  // Map of progress percentages
  progressMap: Map<string, { percent: number; initialMessage?: string }> = new Map();
  // Map of cancel tokens that are used by Axios to cancel requests
  cancelTokenMap: Map<string, CancelTokenSource> = new Map();
  // list of notification keys that have to be closed
  notificationsToClose: string[] = [];

  constructor() {
    makeObservable(this, {
      progressMap: observable,
      cancelTokenMap: observable,
      notificationsToClose: observable,
      clear: action.bound,
      init: action.bound,
      setProgress: action.bound,
      setPercent: action.bound,
      dismiss: action.bound,
      getProgress: action.bound,
      setCancelTokenSource: action.bound,
      getCancelToken: action.bound,
      removeNotificationKey: action.bound,
      closeNotification: action.bound,
    });
  }

  clear() {
    this.progressMap.clear();
    this.cancelTokenMap.clear();
    this.notificationsToClose.length = 0;
  }

  /**
   * Used before network call to initialize cancel token and create observing objects.
   * @param initialMessage - string. The text that is shown before the download was started.
   */
  init(initialMessage?: string): string {
    const uid = getUid();
    this.cancelTokenMap.set(uid, Axios.CancelToken.source());
    // if value is "null" - show message instead of the progress bar.
    const progressValue = initialMessage ? null : 0;
    this.progressMap.set(uid, { percent: progressValue, initialMessage });
    return uid;
  }

  /**
   * Set this function to AxiosRequestConfig.onDownloadProgress/AxiosRequestConfig.onUploadProgress" callbacks.
   * @param uid - uid of progress entry that is given on init().
   * @param evt - Progress event of network call. Returned by Axios
   */
  setProgress(uid: string, evt: AxiosProgressEvent) {
    const progress = Math.round((evt.loaded / evt.total) * 100);
    const record = this.progressMap.get(uid) || { percent: null };
    record.percent = Math.round((evt.loaded / evt.total) * 100);
    this.progressMap.set(uid, record);
    if (progress >= 100) {
      this.closeNotification(uid);
    }
  }

  /**
   * Progress for LongRunner streams (percent 0-100 from BE, not Axios download events).
   */
  setPercent(uid: string, percent: number) {
    const record = this.getProgress(uid);
    if (!record) {
      return;
    }
    record.percent = Math.min(100, Math.max(0, Math.round(percent)));
  }

  closeNotification(uid: string) {
    this.notificationsToClose.push(uid); // add notification key that has to be closed by UI
    this.progressMap.delete(uid);
    this.cancelTokenMap.delete(uid);
  }

  /**
   * Deletes progress record. Use it when download is finished or cancelled.
   * @param uid - uid of progress entry that is given on init().
   */
  dismiss(uid: string) {
    const cancelTokenSource = this.cancelTokenMap.get(uid);
    if (cancelTokenSource) {
      cancelTokenSource.cancel?.();
    }
    this.closeNotification(uid);
    // remove job, in case it was queued from fileDownloadStore
    fileDownloadStore.cancelDownloadJob(uid);
  }

  /**
   * Used by UI component to get percentage for progress bar.
   * @param uid - uid of progress entry that is given on init().
   */
  getProgress(uid: string) {
    return this.progressMap.get(uid);
  }

  /**
   * Used to set custom cancelTokenSource if needed.
   * @param uid - uid of progress entry that is given on init().
   * @param cancelTokenSource - custom cancelTokenSource
   */
  setCancelTokenSource(uid: string, cancelTokenSource: CancelTokenSource) {
    return this.cancelTokenMap.set(uid, cancelTokenSource);
  }

  /**
   * Used to get token that is passed to AxiosRequestConfig.cancelToken
   * @param uid - uid of progress entry that is given on init().
   */
  getCancelToken(uid: string) {
    return this.cancelTokenMap.get(uid)?.token;
  }

  /**
   * Used by UI component to remove key from memory when notification is closed
   * @param uid - uid of progress entry that is given on init().
   */
  removeNotificationKey(uid: string) {
    this.notificationsToClose = this.notificationsToClose.filter((key) => key !== uid);
  }
}

export const actionProgressStore = new ActionProgressStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).actionProgressStore = actionProgressStore;
